import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import './ArticleCard.css';

export interface ArticleCardProps {
  /** Full path to the detail page (e.g. `/pulse/oral-biologics-needle-barrier`). */
  href: string;
  /** Display-friendly publish date (e.g. `Apr 15, 2026`). */
  date: string;
  title: string;
  excerpt: string;
  /** Optional override for the date pill (e.g. `Press Release`). */
  badge?: string;
}

/**
 * Button-style card used on the Pulse and Press index pages.
 *
 * The whole card behaves as a single link so the article title is also the
 * tappable target — both for accessibility (one tab stop) and SEO (descriptive
 * anchor text). Falls back to keyboard activation via Enter / Space.
 */
const ArticleCard: React.FC<ArticleCardProps> = ({ href, date, title, excerpt, badge }) => {
  const navigate = useNavigate();

  const handleActivate = () => {
    navigate(href);
  };

  return (
    <a
      className="article-card"
      href={href}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
          return;
        }
        event.preventDefault();
        handleActivate();
      }}
    >
      <div className="article-card-meta">
        <span className="article-card-date">{badge ?? date}</span>
        {badge && <span className="article-card-date">{date}</span>}
      </div>
      <h2 className="article-card-title">{title}</h2>
      <p className="article-card-excerpt">{excerpt}</p>
      <span className="article-card-cta" aria-hidden="true">
        Read more <ArrowRightOutlined />
      </span>
    </a>
  );
};

export default ArticleCard;
